import { BUTTON_SIZE, BUTTON_TYPE, DSBottomSheet, DSButton, DSModal, DSTooltip } from '@repo/design-system/index'
import { useQuery } from '@tanstack/react-query'
import { isBefore } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import Cookies from 'universal-cookie'
import { GTM_EVENT, sendGTMEvent, useEventTracking } from '../../../helpers/gtmHelper'
import { CurrentUser } from '../../../model/Common/common.interface'
import apis from '../../../modules/apis'
import { axiosPost } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { n_content, n_purchase, n_rwm, s_products } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import urls from '../../../modules/urls'
import { COOKIE_LIST, COOKIE_OPT, IAMPORT_KEY } from '../../../modules/vars'
import { CONTENT_DETAIL_PURCHASE_QUERY } from '../../../sanity/groqQueries'
import { CONTENT_DETAIL_PURCHASE_QUERYResult } from '../../../sanity/types'
import { useBasicPopup } from '../../basic/popup/BasicPopup'
import { isWebinar } from './ContentProgramBody'
import {
  IContentProgramPurchase,
  IPaymentPayload,
  IPurchasePayload,
  PAYMENT_GATEWAY_TYPE,
  PAYMENT_METHOD,
  POPUP_TYPE,
  UnifiedPopup,
  convertDate,
  getButtonAttributes,
  getSessionNumb,
  isEnded,
  usePopup,
} from './ContentTypeUtils'
import SelectPaymentBody from './SelectPaymentBody'
import StripePaymentModalBody from './StripePaymentModalBody'
import { IInitialData } from './types'

interface IContentDetailPurchaseButton {
  initialData: IInitialData
  isReadTogether: boolean
  enrolled: boolean
  refetch
}
const TOOLTIP_STATUS = {
  MORE_THAN_24: 'before_more_than_24_hours_to_before_24_hours', //시작시간 기준 24시간 보다 더 전
  FROM_24_TO_15MIN: 'before_24_hours_to_before_15_minutes', // 24시간 전 ~ 15분 전
  FROM_15_TO_START: 'before_15_minutes_to_start_time', // 15분 전 ~ 시작시간
  FROM_START_TO_END: 'ongoing', // 시작 시간 ~ 시간 시간 + 1시간
  ENDED: 'ended', // 시간시간 + 1시간 ~ 이후
}

const renderTooltipText = (t, tooltipStatus, hours, minutes, isWebinarRT, isLocaleKO) => {
  if (isWebinarRT) {
    switch (tooltipStatus) {
      case TOOLTIP_STATUS.FROM_24_TO_15MIN:
        return hours == 0
          ? isLocaleKO
            ? `${minutes}분 후 웨비나가 시작됩니다.`
            : `${minutes} minutes until the live webinar.`
          : isLocaleKO
          ? `${hours}시간 ${minutes}분 후 웨비나가 시작됩니다.`
          : `${hours} hours ${minutes} minutes until the live webinar.`
      case TOOLTIP_STATUS.FROM_15_TO_START:
        return isLocaleKO
          ? `웨비나가 곧 시작됩니다. 입장해 주세요.`
          : `The live webinar is about to start. Please enter the Zoom meeting.`
      case TOOLTIP_STATUS.FROM_START_TO_END:
        return isLocaleKO
          ? `웨비나가 진행 중입니다. 입장해 주세요.`
          : `The live webinar is in progress. Please enter the Zoom meeting.`
      default:
        return ''
    }
  } else {
    switch (tooltipStatus) {
      case TOOLTIP_STATUS.FROM_24_TO_15MIN:
        return hours == 0 ? t(n_rwm.msg_15min(minutes)) : t(n_rwm.msg_24hrs(hours, minutes))
      case TOOLTIP_STATUS.FROM_15_TO_START:
        return t(n_rwm.msg_start)
      case TOOLTIP_STATUS.FROM_START_TO_END:
        return t(n_rwm.msg_late)
      default:
        return ''
    }
  }
}

const calculateRemainingTime = (currentTime, startTime) => {
  const diff = startTime - currentTime // 밀리초 단위로 남은 시간 계산

  const hours = Math.floor(diff / (1000 * 60 * 60)) // 시간으로 변환
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)) // 남은 밀리초를 분으로 변환

  return { hours, minutes }
}

const ContentDetailPurchaseButton = ({
  initialData,
  isReadTogether,
  enrolled,
  refetch,
}: IContentDetailPurchaseButton) => {
  const router = useAppRouter()

  const isWebview = useIsWebview()
  const cookies = new Cookies()
  const { currentUser } = useCurrentUser()
  const { t, locale, isLocaleKO } = useTranslation()
  const { popup, openPopup, closePopup } = usePopup()
  const basicPopup = useBasicPopup()
  const isMobile = useIsMobile()

  const { _id: id, price, _type: contentType, title, liveWebinarDateList, endDate, dueDate, startDate } = initialData
  const dateList = isReadTogether ? liveWebinarDateList?.liveWebinarDate : null
  const session = isReadTogether ? getSessionNumb(dateList) : null
  const MIN_AMOUNT = 1000
  const slug = router.query.slug

  const start = new Date(startDate)
  const today = new Date()
  const isBeforeStart = isBefore(today, start)

  const [buttonAttributes, setButtonAttributes] = useState({
    label: t(n_content.rwm.btn),
    disabled: true,
    onClick: null,
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [link, setLink] = useState('')
  const { sendClickEvent } = useEventTracking()

  const [showSelectPayment, setShowSelectPayment] = useState(false)
  const [showPaymentStripe, setShowPaymentStripe] = useState(false)
  // ================== 버튼/툴팁 Active 조건 ===========================
  const { label, disabled, onClick } = buttonAttributes

  const [isActiveButton, setIsActiveButton] = useState(false)
  const [tooltipStatus, setTooltipStatus] = useState(TOOLTIP_STATUS.MORE_THAN_24)
  const [tooltipRemainTime, setTooltipRemainTime] = useState({ hours: 0, minutes: 0 })

  const tooltipActive =
    tooltipStatus === TOOLTIP_STATUS.FROM_24_TO_15MIN ||
    tooltipStatus === TOOLTIP_STATUS.FROM_15_TO_START ||
    tooltipStatus === TOOLTIP_STATUS.FROM_START_TO_END

  const isActiveButtonTime = useCallback(
    (currentTime) => {
      if (!dateList) return true

      return dateList.some((time) => {
        const timeInMs = new Date(time).getTime()
        const startTime = timeInMs - 15 * 60 * 1000 // 15 minutes before
        const endTime = timeInMs + 60 * 60 * 1000 // 1 hour after Conference: +5h 30m after
        return currentTime >= startTime && currentTime <= endTime
      })
    },
    [dateList]
  )

  const isActiveTooltipTime = useCallback(
    (currentTime) => {
      if (!dateList) return TOOLTIP_STATUS.ENDED

      for (const time of dateList) {
        const timeInMs = new Date(time).getTime()
        const before24HourTime = timeInMs - 24 * 60 * 60 * 1000
        const before15MinuteTime = timeInMs - 15 * 60 * 1000
        const endTime = timeInMs + 60 * 60 * 1000

        if (currentTime <= endTime) {
          if (currentTime <= before24HourTime) {
            return TOOLTIP_STATUS.MORE_THAN_24
          } else if (currentTime >= before24HourTime && currentTime <= before15MinuteTime) {
            const remainingTime = calculateRemainingTime(currentTime, timeInMs)
            setTooltipRemainTime({ hours: remainingTime.hours, minutes: remainingTime.minutes })
            return TOOLTIP_STATUS.FROM_24_TO_15MIN
          } else if (currentTime >= before15MinuteTime && currentTime <= timeInMs) {
            return TOOLTIP_STATUS.FROM_15_TO_START
          } else if (currentTime >= timeInMs && currentTime <= endTime) {
            return TOOLTIP_STATUS.FROM_START_TO_END
          }
        }
      }
      return TOOLTIP_STATUS.ENDED
    },
    [dateList]
  )

  useEffect(() => {
    const checkActiveTime = () => {
      const currentTime = new Date().getTime()
      setIsActiveButton(isActiveButtonTime(currentTime))
      setTooltipStatus(isActiveTooltipTime(currentTime))
      if (!link) fetchLink()
    }

    const interval = setInterval(checkActiveTime, 30000)

    checkActiveTime()

    return () => {
      clearInterval(interval)
    }
  }, [enrolled])

  // ================== Get Submit Link ===========================

  const {
    data,
    isFetching: linkFetching,
    refetch: fetchLink,
  } = useQuery({
    queryKey: ['submitLink', currentUser?.id, id],
    queryFn: () => fetchLinkData(slug),
    enabled: !!currentUser && isActiveButton && enrolled,
  })

  const getParams = (slug, isReadTogether) => ({
    slug,
    contentType: isReadTogether ? 'contentReadTogether' : 'contentReadAlone',
  })

  const fetchLinkData = useCallback(
    async (slug) => {
      if (!currentUser || !slug) return
      try {
        const params = getParams(slug, isReadTogether)
        const data: CONTENT_DETAIL_PURCHASE_QUERYResult = await client.fetch(CONTENT_DETAIL_PURCHASE_QUERY, params)
        return data
      } catch (e) {
        console.error('Error fetching link data:', e)
        alert(e.response?.data?.error || e.message)
      }
    },
    [currentUser, isReadTogether]
  )

  useEffect(() => {
    if (data && !linkFetching) {
      setLink(data.submitButtonLink)
    }
  }, [data, linkFetching])

  // ================== Main Handler ==================
  const handleApplyButton = async (e: React.MouseEvent, currentUser: CurrentUser) => {
    e.preventDefault()

    sendClickEvent({ button_name: 'register_reading_program' })

    //Checking out Login Status
    if (!currentUser) {
      openPopup({
        title: t(n_rwm.modal_login.title),
        message: t(n_rwm.modal_login.desc),
        type: POPUP_TYPE.LOGIN,
        onConfirm: handleLoginButton,
      })
      return
    }

    if (loading) return

    initiatePaymentProcess(currentUser)
  }

  const handleOpenPurchaseMethodSelectModal = (currentUser) => {
    //Checking out Login Status
    if (!currentUser) {
      openPopup({
        title: t(n_rwm.modal_login.title),
        message: t(n_rwm.modal_login.desc),
        type: POPUP_TYPE.LOGIN,
        onConfirm: handleLoginButton,
      })
      return
    }

    if (isMobile) {
      setShowSelectPayment(true)
    } else {
      openPopup({
        title: t(n_purchase.payment.title_choose),
        message: (
          <div className="mt-[32px]">
            <SelectPaymentBody
              handleDomesticPayment={(e) => handleApplyButton(e, currentUser)}
              handleInternationalPayment={() => {
                closePopup()
                basicPopup.show({
                  maxW: 464,
                  id: 'stripe-payment-modal',
                  title: t(n_purchase.payment.foreign_card),
                  body: <StripePaymentModalBody price={price} id={id} contentType={contentType} />,
                })
              }}
            />
          </div>
        ),
        type: POPUP_TYPE.SELECT_PAYMENT,
      })
    }
  }

  const initiatePaymentProcess = (currentUser: CurrentUser) => {
    setLoading(true)
    window.scrollTo(0, 0)

    const purchasePayload: IPurchasePayload = {
      sanity_content_type: contentType,
      sanity_id: id,
      transaction_type: 'iamport_auth',
      amount: price,
      currency: 'krw',
    }
    //1. 결제 데이터 생성 createPurchase -> POST /v4/student/content_program_purchases
    createPurchase(purchasePayload).then((purchaseResponse) => {
      if (purchaseResponse) {
        const paymentPayload: IPaymentPayload = {
          purchase_id: purchaseResponse.content_program_purchase.id,
          merchant_uid: purchaseResponse.content_program_purchase.merchant_uid,
          currentUser: currentUser,
        }

        //2. 아임포트 결제요청 initializePayment -> IMP.request_pay
        //3. 결제 체크아웃 initializePayment -> POST /v4/student/content_program_purchases/${sanityId}/checkout
        initializePayment(paymentPayload)
      }
    })
  }

  // ================== API Async Func Creation ==================
  const createPurchase = async (payload: IPurchasePayload) => {
    try {
      if (payload.amount < MIN_AMOUNT) {
        alert(t(s_products.purchase_must_be))
        window.location.reload()
        return false
      }

      const url = apis.contentPrograms.purchase()

      const response: IContentProgramPurchase = await axiosPost(url, payload, { shouldThrowError: true })
      if (response && response.success) {
        return response
      }
    } catch (error) {
      setLoading(false)
      openPopup({
        title: t(n_rwm.modal_payment.title),
        message: error?.response?.data?.error,
        type: POPUP_TYPE.FAIL,
      })
      return null
    }
  }

  // ================== Payment Execution ==================
  const getImpPayload = (
    pg: string,
    method: string,
    currentUser: CurrentUser,
    purchase_id: number,
    merchant_uid: string
  ) => ({
    pg: pg,
    pay_method: method,
    merchant_uid: merchant_uid, // 넘기면 결과물로 나오는구나 imp
    name: title,
    buyer_email: currentUser.profile?.email || currentUser.email,
    buyer_name: currentUser.profile?.name || currentUser.name,
    buyer_tel: currentUser.profile?.mobileNumber || currentUser.phone,
    amount: price,
    m_redirect_url: `${window.location.origin}/${locale}${
      isWebview ? urls.student.newPortal.study.readPricing.redirect : urls.student.landing.study.readPricing.redirect
    }?isReadTogether=${isReadTogether}&purchaseId=${purchase_id}&isWebview=${isWebview}`,
    vbank_due: null,
    custom_data: {
      price,
      env: window?.location?.host === 'www.ringleplus.com' ? 'production' : 'qa',
    },
  })

  const initializePayment = ({ currentUser, purchase_id, merchant_uid }: IPaymentPayload) => {
    const IMP = window.IMP
    IMP.init(IAMPORT_KEY)
    // 2. 아임포트 결제 요청 IMP.request_pay
    IMP.request_pay(
      getImpPayload(PAYMENT_GATEWAY_TYPE.UPLUS, PAYMENT_METHOD.CARD, currentUser, purchase_id, merchant_uid),
      (response) =>
        // 3. 결제 체크아웃 POST /v4/student/content_program_purchases/${purchase_id}/checkout
        handlePaymentResponse(response, purchase_id)
    )
  }

  const handlePaymentResponse = async (response, purchase_id: number) => {
    setLoading(false)

    // 결제 성공 케이스
    if (!isWebview && response.success) {
      try {
        const currentTime = new Date().getTime()
        const url = apis.contentPrograms.purchaseCheckout(purchase_id)
        const checkoutResponse = await axiosPost(url, null, { shouldThrowError: true })
        if (checkoutResponse && checkoutResponse.success) {
          // fbPixel.webinar_enroll
          sendGTMEvent(GTM_EVENT.WEBINAR_ENROLL)

          setIsActiveButton(isActiveButtonTime(currentTime))
          refetch()
          openPopup({
            title: isWebinar(router.query.slug)
              ? isLocaleKO
                ? `웨비나 신청이 완료되었습니다.`
                : `Registration to the webinar is complete.`
              : isReadTogether
              ? t(n_rwm.modal_rwm_done.title)
              : t(n_rwm.modal_ss_done.title),
            message: isWebinar(router.query.slug)
              ? isLocaleKO
                ? `웨비나는 ${convertDate(
                    dateList[0]
                  )}에 시작합니다. 이메일 주소는 [알림 수신 설정]에서 변경할 수 있습니다.`
                : `The first live webinar is scheduled to start on ${convertDate(
                    dateList[0]
                  )} KST. You can change the email address to receive instructions and materials in [Notification Settings].`
              : isReadTogether
              ? t(n_rwm.msg_ra_done_2(convertDate(dateList[0])))
              : t(n_rwm.msg_ra_done_1),
            type: POPUP_TYPE.SUCCESS,
          })
        } else {
          openPopup({
            title: t(n_rwm.modal_payment.title),
            message: checkoutResponse?.error,
            type: POPUP_TYPE.FAIL,
          })
        }
      } catch (error) {
        openPopup({
          title: t(n_rwm.modal_payment.title),
          message: error?.response?.data?.error,
          type: POPUP_TYPE.FAIL,
        })
      }
      //결제 실패/취소 케이스
    } else {
      console.info('결제실패', currentUser.id, response)

      if (response.error_msg.includes('사용자가 결제를 취소') || response.error_code == 'S053') {
        openPopup({
          title: t(n_rwm.modal_payment.title),
          message: response.error_msg,
          type: POPUP_TYPE.FAIL,
        })
      } else if (response.error_msg == '[결제실패] 사용자의 브라우저가 팝업을 허용하고 있지 않습니다.') {
        openPopup({
          title: t(n_rwm.modal_payment.title),
          message: t(n_rwm.msg_pop_up),
          type: POPUP_TYPE.FAIL,
        })
      } else {
        openPopup({
          title: t(n_rwm.modal_payment.title),
          message: t(n_rwm.msg_retry),
          type: POPUP_TYPE.FAIL,
        })
      }
    }
  }

  const handleLoginButton = () => {
    sendClickEvent({ button_name: 'register_reading_program' })
    cookies.set(COOKIE_LIST.LOGIN_AFTER_URL, router.asPath, COOKIE_OPT)
    openPopup({
      title: t(n_rwm.modal_login.title),
      message: t(n_rwm.modal_login.desc),
      type: POPUP_TYPE.LOGIN,
      onConfirm: () => router.push(urls.student.auth.signIn),
    })
    return
  }

  const handleEnter = (link) => {
    isWebview ? router.linking(link) : window.open(link, '_blank')
  }

  useEffect(() => {
    const attributes = getButtonAttributes({
      t,
      isActive: isActiveButton,
      isEndedEvent: isEnded(endDate),
      isEndedDueDate: isEnded(dueDate),
      enrolled,
      link,
      handleApplyButton: handleOpenPurchaseMethodSelectModal,
      currentUser,
      session,
      handleLoginButton,
      path: router.asPath,
      isReadTogether,
      isBeforeStart,
      handleEnter,
    })

    setButtonAttributes(attributes)
  }, [tooltipStatus, linkFetching, link, currentUser, isActiveButton, refetch, enrolled, isReadTogether, isBeforeStart])

  const isWebinarRT = isWebinar(router.query.slug)

  return (
    <>
      {enrolled && tooltipActive ? (
        <DSTooltip
          place={'top'}
          hasCloseButton
          isMultiline={false}
          triggerComponent={
            <DSButton type={BUTTON_TYPE.SOLID} disabled={disabled} size={BUTTON_SIZE.MD} onClick={onClick}>
              <DSButton.Label>{label}</DSButton.Label>
            </DSButton>
          }
          contentComponent={renderTooltipText(
            t,
            tooltipStatus,
            tooltipRemainTime.hours,
            tooltipRemainTime.minutes,
            isWebinarRT,
            isLocaleKO
          )}
        />
      ) : (
        <DSButton type={BUTTON_TYPE.SOLID} disabled={disabled} size={BUTTON_SIZE.MD} onClick={onClick}>
          <DSButton.Label>{label}</DSButton.Label>
        </DSButton>
      )}

      <UnifiedPopup
        isOpen={popup.show}
        setIsOpen={closePopup}
        type={popup.type}
        title={popup.title}
        content={popup.message}
        onConfirm={popup.onConfirm}
      />
      {showSelectPayment && (
        <DSBottomSheet
          id="TextOnlySMTwoButtonHorizontalPopup"
          isOpen={showSelectPayment}
          setIsOpen={setShowSelectPayment}
          closeButton={true}
          closeOnDim={false}
          enableScroll={true}
        >
          <DSModal.Title>
            <span className="Title2-18Bd px-[20px] mt-[4px]">{t(n_purchase.payment.title_choose)}</span>
          </DSModal.Title>
          <DSModal.Content className="mt-[32px] px-[20px] pb-[34px]">
            <SelectPaymentBody
              handleDomesticPayment={(e) => handleApplyButton(e, currentUser)}
              handleInternationalPayment={() => {
                closePopup()
                setShowPaymentStripe(true)
                setShowSelectPayment(false)
              }}
            />
          </DSModal.Content>
        </DSBottomSheet>
      )}
      {showPaymentStripe && (
        <DSBottomSheet
          enableScroll={true}
          closeOnDim={false}
          id="TextOnlySMTwoButtonHorizontalPopup1"
          isOpen={showPaymentStripe}
          setIsOpen={setShowPaymentStripe}
          closeButton={true}
        >
          <DSModal.Title>
            <span className="Title2-18Bd px-[20px] mt-[4px]">{t(n_purchase.payment.foreign_card)}</span>
          </DSModal.Title>
          <DSModal.Content className="mt-[16px] p-[20px] pb-[34px]">
            <StripePaymentModalBody price={price} id={id} contentType={contentType} />
          </DSModal.Content>
        </DSBottomSheet>
      )}
    </>
  )
}

export default ContentDetailPurchaseButton
